// Statistical models for bus schedule analysis
// Each function takes: (actualTimes, scheduledTime, otrWindow)
// Returns: { recommendedTime, percentInWindow }

// Lanczos approximation for the Gamma function
function gamma(z) {
  // Only valid for z > 0
  const g = 7;
  const p = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7
  ];
  if(z < 0.5) {
    // Reflection formula
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  } else {
    z -= 1;
    let x = p[0];
    for(let i = 1; i < g + 2; i++) {
      x += p[i] / (z + i);
    }
    const t = z + g + 0.5;
    return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
  }
}

// Helper: CDF and quantile for normal distribution
function normalCDF(x, mean, std) {
  return 0.5 * (1 + Math.erf((x - mean) / (std * Math.sqrt(2))));
}
function normalQuantile(p, mean, std) {
  // Approximate inverse CDF (probit)
  // Use a simple approximation for now
  // For production, use a library
  const a = Math.sqrt(2) * std;
  return mean + a * inverseErf(2 * p - 1);
}
function inverseErf(x) {
  // Approximate inverse error function
  // Abramowitz and Stegun formula 7.1.26
  const a = 0.147;
  const ln = Math.log(1 - x * x);
  const first = 2 / (Math.PI * a) + ln / 2;
  const second = ln / a;
  return (x > 0 ? 1 : -1) * Math.sqrt(Math.sqrt(first * first - second) - first);
}

// Normal Model
export function normalModel(actualTimes, scheduledTime, otrWindow) {
  // actualTimes: ms since midnight
  // scheduledTime: ms since midnight
  // otrWindow: [earlySec, lateSec] (e.g., [-30, 120])
  if (!actualTimes.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const diffs = actualTimes.map(t => t - scheduledTime);
  const mean = diffs.reduce((a, b) => a + b, 0) / diffs.length;
  const std = Math.sqrt(diffs.reduce((a, b) => a + (b - mean) ** 2, 0) / diffs.length);
  // Find the scheduled time that maximizes percent in window
  // For normal, this is when the window is centered on the mean
  // Try shifting scheduledTime so that mean falls at center of window
  const [early, late] = otrWindow;
  const windowCenter = (late + early) / 2 * 1000;
  const recommendedTime = scheduledTime + mean - windowCenter;
  // Compute percent in window
  const lower = early * 1000;
  const upper = late * 1000;
  const percentInWindow = normalCDF(upper, mean, std) - normalCDF(lower, mean, std);
  return { recommendedTime, percentInWindow };
}

// Empirical Model
export function empiricalModel(actualTimes, scheduledTime, otrWindow) {
  if (!actualTimes.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const [early, late] = otrWindow;
  // Try a range of candidate scheduled times
  let bestPercent = 0;
  let bestShifts = [];
  const minTime = Math.min(...actualTimes) - late;
  const maxTime = Math.max(...actualTimes) - early;
  const step = 1000; // 1 second
  for (let sched = minTime; sched <= maxTime; sched += step) {
    const count = actualTimes.filter(t => (t - sched) >= early && (t - sched) <= late).length;
    const percent = count / actualTimes.length;
    if (percent > bestPercent) {
      bestPercent = percent;
      bestShifts = [sched];
    } else if (percent === bestPercent) {
      bestShifts.push(sched);
    }
  }
  // If multiple shifts yield the same bestPercent, pick the one closest to the median of actuals
  let recommendedTime = bestShifts[0];
  if (bestShifts.length > 1) {
    const sorted = [...actualTimes].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    recommendedTime = bestShifts.reduce((prev, curr) =>
      Math.abs(curr - median) < Math.abs(prev - median) ? curr : prev
    );
  }
  return { recommendedTime, percentInWindow: bestPercent };
}

// Log-Normal Model
export function logNormalModel(actualTimes, scheduledTime, otrWindow) {
  if (!actualTimes.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const diffs = actualTimes.map(t => t - scheduledTime).filter(d => d > 0);
  if (!diffs.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const logs = diffs.map(Math.log);
  const mean = logs.reduce((a, b) => a + b, 0) / logs.length;
  const std = Math.sqrt(logs.reduce((a, b) => a + (b - mean) ** 2, 0) / logs.length);
  const [early, late] = otrWindow;
  // Find scheduled time so that log-normal window is centered on mean
  const windowCenter = (late + early) / 2 * 1000;
  const recommendedTime = scheduledTime + Math.exp(mean) - windowCenter;
  // Compute percent in window
  const lower = Math.log(Math.max(1, early * 1000));
  const upper = Math.log(late * 1000);
  const normalCDFLog = (x) => 0.5 * (1 + Math.erf((x - mean) / (std * Math.sqrt(2))));
  const percentInWindow = normalCDFLog(upper) - normalCDFLog(lower);
  return { recommendedTime, percentInWindow };
}

// Weibull Model (2-parameter, shape k, scale λ)
export function weibullModel(actualTimes, scheduledTime, otrWindow) {
  if (!actualTimes.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  // Use method of moments for k, lambda
  const diffs = actualTimes.map(t => t - scheduledTime).filter(d => d > 0);
  if (!diffs.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const n = diffs.length;
  const mean = diffs.reduce((a, b) => a + b, 0) / n;
  const ln = diffs.map(Math.log);
  const meanLn = ln.reduce((a, b) => a + b, 0) / n;
  // Approximate k using Newton-Raphson (fixed 5 iterations)
  let k = 1.0;
  for (let i = 0; i < 5; i++) {
    const f = Math.log(mean) - meanLn + (1 / k) - Math.log(k);
    const fprime = -1 / (k * k) - 1 / k;
    k = k - f / fprime;
    if (k <= 0) k = 0.1;
  }
  const lambda = mean / gamma(1 + 1 / k);
  // Find recommended time (center window on mean)
  const [early, late] = otrWindow;
  const windowCenter = (late + early) / 2 * 1000;
  const recommendedTime = scheduledTime + mean - windowCenter;
  // Percent in window
  const weibullCDF = (x) => 1 - Math.exp(-Math.pow(x / lambda, k));
  const lower = Math.max(0, early * 1000);
  const upper = late * 1000;
  const percentInWindow = weibullCDF(upper) - weibullCDF(lower);
  return { recommendedTime, percentInWindow };
}

// Robust Model (Median/IQR)
export function robustModel(actualTimes, scheduledTime, otrWindow) {
  if (!actualTimes.length) return { recommendedTime: scheduledTime, percentInWindow: 0 };
  const diffs = actualTimes.map(t => t - scheduledTime);
  const sorted = [...diffs].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const [early, late] = otrWindow;
  const windowCenter = (late + early) / 2 * 1000;
  const recommendedTime = scheduledTime + median - windowCenter;
  // Percent in window
  const count = diffs.filter(d => d >= early * 1000 && d <= late * 1000).length;
  const percentInWindow = count / diffs.length;
  return { recommendedTime, percentInWindow };
} 